import { UserName, UserProfile } from '#components/UserProfile.tsx'
import { useAuth } from '#context/Auth.tsx'
import { useStatus, type Status as tStatus } from '#context/Status.tsx'
import cx from 'classnames'
import { useEffect, useState } from 'preact/hooks'
import { decodeTime } from 'ulid'
import { Ago } from './Ago.tsx'
import {
	AddIcon,
	AddReactionIcon,
	CloseIcon,
	DeleteIcon,
	EditIcon,
	PersistencePendingIcon,
	SubMenuIcon,
} from './Icons.tsx'
import { Markdown } from './Markdown.tsx'
import { Reaction, SelectReaction } from './Reactions.tsx'
import { CustomReaction } from './CustomReaction.tsx'

export const Status = ({
	status,
	onReactionsVisible,
}: {
	status: tStatus
	onReactionsVisible?: (visible: boolean) => void
}) => {
	const [reactionsVisible, showReactions] = useState(false)
	const [customReactionVisible, showCustomReaction] = useState(false)
	const [operationsVisible, showOperations] = useState(false)
	const { user } = useAuth()
	const { addReaction, deleteReaction, deleteStatus } = useStatus()
	const canEdit = user?.id === status.author
	const hasOperations = canEdit

	useEffect(() => {
		onReactionsVisible?.(reactionsVisible)
	}, [reactionsVisible, onReactionsVisible])

	useEffect(() => {
		if (!reactionsVisible) return

		const t = setTimeout(() => {
			showReactions(false)
		}, 5000)

		return () => {
			clearTimeout(t)
		}
	}, [reactionsVisible])

	useEffect(() => {
		if (!operationsVisible) return

		const t = setTimeout(() => {
			showOperations(false)
		}, 5000)

		return () => {
			clearTimeout(t)
		}
	}, [operationsVisible])

	return (
		<>
			<div>
				<small class="text-muted opacity-75 d-flex me-2 mb-2 flex-wrap">
					<span class="text-nowrap d-flex align-items-center">
						<a
							href={`/project/${encodeURIComponent(
								status.project,
							)}/status/${encodeURIComponent(status.id)}`}
							class="text-muted"
						>
							<Ago date={new Date(decodeTime(status.id))} />
						</a>
						{status.version > 1 && (
							<>
								<EditIcon size={20} class={'ms-1'} /> {status.version}
							</>
						)}
					</span>
					<span class="mx-1">&middot;</span>
					<span class="text-nowrap d-flex align-items-center">
						{status.attributeTo !== undefined && (
							<>
								{status.attributeTo} (
								<span>
									attributed by <UserName id={status.author} />
								</span>
								)
							</>
						)}
						{status.attributeTo === undefined && (
							<UserProfile id={status.author} />
						)}
					</span>
				</small>
			</div>
			<Markdown markdown={status.message} />
			<div
				class={cx(
					'd-flex align-items-center justify-content-between mb-3 flex-wrap',
					{
						'mt-2': status.reactions.length > 0,
					},
				)}
			>
				<div>
					{status.reactions.map((reaction) => {
						const isAuthor = reaction.author === user?.id
						return (
							<Reaction
								key={reaction.id}
								reaction={reaction}
								onClick={() => {
									if (isAuthor) {
										deleteReaction(status, reaction)
									}
								}}
							/>
						)
					})}
					{!customReactionVisible && (
						<>
							{!reactionsVisible && (
								<button
									type="button"
									class="btn btn-light"
									onClick={() => showReactions(true)}
								>
									<AddReactionIcon size={18} />
								</button>
							)}
							{reactionsVisible && (
								<>
									<button
										type="button"
										class="btn btn-light"
										onClick={() => showReactions(false)}
									>
										<CloseIcon size={18} />
									</button>
									<SelectReaction
										onReaction={(reaction) => {
											addReaction(status, reaction)
										}}
									/>
									<button
										type="button"
										class="btn btn-light me-1"
										onClick={() => {
											showReactions(false)
											showCustomReaction(true)
										}}
									>
										<AddIcon size={18} />
									</button>
								</>
							)}
						</>
					)}
				</div>
				{!reactionsVisible && !customReactionVisible && (
					<div class="text-nowrap">
						{status.persisted === false && (
							<PersistencePendingIcon class="me-1" />
						)}
						{hasOperations && !operationsVisible && (
							<button
								type="button"
								class="btn btn-light me-1"
								onClick={() => showOperations(true)}
							>
								<SubMenuIcon size={18} />
							</button>
						)}
						{operationsVisible && (
							<>
								{canEdit && (
									<>
										<button
											type="button"
											class="btn btn-outline-danger me-1"
											onClick={() => {
												deleteStatus(status)
											}}
										>
											<DeleteIcon size={18} />
										</button>
										<a
											class="btn btn-outline-secondary me-1"
											href={`/project/${encodeURIComponent(
												status.project,
											)}/status/${encodeURIComponent(status.id)}/edit`}
										>
											<EditIcon size={18} />
										</a>
									</>
								)}
								<button
									type="button"
									class="btn btn-light me-1"
									onClick={() => showOperations(false)}
								>
									<CloseIcon size={18} />
								</button>
							</>
						)}
					</div>
				)}
			</div>
			{customReactionVisible && (
				<CustomReaction
					onReaction={(reaction) => {
						addReaction(status, reaction)
						showCustomReaction(false)
					}}
					onClose={() => {
						showCustomReaction(false)
					}}
				/>
			)}
		</>
	)
}
